import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { type ClientPassage } from '../dm/Passage.js';
import { db } from '../model/db.js';
import { getTempPassages } from '../stores/UISlice.js';
import { resolvePassageHashtagIds } from '../sync/PassageSync.js';
import { useAsyncLiveQuery } from './useAsyncLiveQuery.js';

/**
 * Live-reactive lookup of a single passage by id, for a component that
 * needs full passage details (osis/version/hashtags) given only an id -
 * used instead of passing a `ClientPassage` snapshot down through props,
 * which otherwise goes stale relative to background sync writes.
 *
 * Temp (pinned, unsaved) passages live only in Redux, not Dexie, so those
 * are looked up there directly (already reactive via `useSelector`). Saved
 * passages are live-queried against Dexie, keyed specifically by id (and,
 * for hashtags, by the exact ids referenced) - Dexie's live-query
 * dependency tracking scopes invalidation to those specific keys, so a
 * write to a *different* passage or hashtag doesn't cause this to re-run.
 *
 * `id` may be `undefined` (e.g. nothing currently selected) so this can
 * always be called unconditionally, satisfying the rules of hooks.
 */
export const usePassageById = (id: string | undefined, isTemp: boolean): ClientPassage | undefined => {
  const tempPassages = useSelector(getTempPassages);

  const { data: localPassage } = useAsyncLiveQuery(
    () => (id === undefined || isTemp ? Promise.resolve(undefined) : db.passages.get(id)),
    [id, isTemp],
    undefined
  );

  const hashtagIdsKey = (localPassage?.hashtagIds ?? []).join(',');

  // dexie-react-hooks' useLiveQuery keeps showing the *previous* resolved
  // value (marked as settled, not pending) while a new deps-triggered
  // subscription is still catching up - it never exposes a "this is stale"
  // signal of its own, so the querier stamps its result with the key it
  // was actually computed for, and we compare that against the *current*
  // key ourselves to know whether `resolvedTags` has caught up yet
  const { data: resolvedTags } = useAsyncLiveQuery(
    async () => {
      if (hashtagIdsKey.length === 0) return { forKey: hashtagIdsKey, tags: [] as string[] };

      const rows = await resolvePassageHashtagIds(hashtagIdsKey.split(','));
      return { forKey: hashtagIdsKey, tags: rows.map((row) => row.tag) };
    },
    [hashtagIdsKey],
    { forKey: '', tags: [] }
  );

  const tagsAreFresh = resolvedTags.forKey === hashtagIdsKey;

  // `id` is stable for a hook instance's whole lifetime (the caller keys
  // its component on id - see PassageCards.tsx), so once this has fully
  // resolved once, it never should again: `resolvedTags` goes momentarily
  // stale whenever hashtagIds changes (e.g. a background sync attaching
  // another tag), and flipping back to `undefined` here would make an
  // *already-open* card's wrapper unmount it, losing any in-progress edit
  // - only the very first resolution needs to wait for both to land together
  const hasResolvedRef = useRef(false);
  const stillResolvingFirstLoad = !tagsAreFresh && !hasResolvedRef.current;
  if (localPassage && tagsAreFresh) {
    hasResolvedRef.current = true;
  }

  if (id === undefined) return undefined;

  if (isTemp) {
    return tempPassages.find((p) => p.id === id);
  }

  if (!localPassage || stillResolvingFirstLoad) return undefined;

  return {
    id: localPassage.id,
    osis: localPassage.osis,
    version: localPassage.version,
    updateDate: localPassage.updateDate,
    bibleViewOptions: localPassage.bibleViewOptions,
    isTemp: false,
    hashtags: resolvedTags.tags.length > 0 ? resolvedTags.tags : undefined,
  };
};
